/**
 * Rango Quantum — wiring del indicador.
 *
 * Indicador con señales (copytrading comunidad): port a JS del filtro Pine
 * "ZEC 1 y 4H QUANTUM RANGO 2" (engine.js, paridad cruzada validada contra
 * el motor Python en engine.parity.test.js), niveles de TP/liquidación de la
 * config de serie CONGELADA (constants.js) y presentación Quantum (adapter +
 * stats + renderer), todo vía createChartDrawingApi.
 */
import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import { SAFE_DEFAULTS, resolveConfig } from './constants.js'
import { runAdapter } from './adapter.js'
import { computeStats } from './stats.js'
import { renderRangoQuantum } from './renderer.js'
import { WARMUP_BARS } from './engine.js'
import { computePriceFormat } from '../../utils/pricePrecision.js'

export const defaultConfig = SAFE_DEFAULTS

export { resolveConfig }

function gatedResult() {
  return { signals: [], activeSignal: null, lastSignal: null, stats: null, gated: true }
}

/** Sanea velas de entrada: OHLCV numéricos y time presente. */
function sanitizeCandles(candles) {
  if (!Array.isArray(candles)) return null
  const out = []
  for (const c of candles) {
    if (c == null || typeof c !== 'object') continue
    const open = Number(c.open)
    const high = Number(c.high)
    const low = Number(c.low)
    const close = Number(c.close)
    if (!Number.isFinite(open) || !Number.isFinite(high) || !Number.isFinite(low) || !Number.isFinite(close)) continue
    if (c.time == null || c.time === '') continue
    out.push({ time: c.time, open, high, low, close, volume: Number.isFinite(Number(c.volume)) ? Number(c.volume) : 0 })
  }
  return out
}

// symbol/timeframe (opcionales, 3º/4º arg): activo y TF que el usuario está
// viendo AHORA MISMO en el chart — modelo de alcance (Marc, 2026-08-14,
// validación en pantalla): todo lo que muestra el badge/panel (señal activa,
// niveles, estadísticas) se refiere SIEMPRE al símbolo+TF visible, calculado
// por el motor JS sobre `candles` (que YA vienen filtradas a ese símbolo+TF
// por el caller) — nunca datos del motor de backend (ese evalúa 4H fijo para
// TODOS los símbolos, es un proceso independiente para señales/Telegram, no
// para pintar el chart). symbol/timeframe aquí NO filtran datos — ya vienen
// filtrados — solo alimentan el aviso de recomendación (buildRecommendation:
// TF≠4H o símbolo fuera de la cesta del estudio).
export function detect(candles, rawConfig, symbol, timeframe) {
  try {
    const cfg = resolveConfig(rawConfig)
    if (cfg.enabled === false) return gatedResult()

    const clean = sanitizeCandles(candles)
    // +2, no +1: runAdapter descarta la última vela (en formación/no
    // confirmada, ver adapter.js) antes de calcular — hacen falta
    // WARMUP_BARS velas cerradas MÁS la última (viva o no) que se descarta.
    if (!clean || clean.length < WARMUP_BARS + 2) return gatedResult()

    const out = runAdapter(clean, cfg)
    // B3 (validación 2026-08-15): misma precisión que el eje nativo del
    // chart — computePriceFormat sobre las MISMAS velas que ve la serie
    // principal, no una regla de umbrales propia (evita el desajuste
    // "69610.95" en el eje vs "69611.0" en el marcador/badge).
    const pricePrecision = computePriceFormat(clean).precision
    return { ...out, stats: computeStats(out.signals, cfg, symbol, timeframe), showLastSignals: cfg.showLastSignals, pricePrecision }
  } catch {
    return gatedResult()
  }
}

export function render(chart, candleSeries, result, config, api) {
  const cfg = resolveConfig(config)
  renderRangoQuantum(candleSeries, result, cfg, api)
}

export default {
  id: 'rango_quantum',
  name: 'Rango Quantum',
  version: '1.0.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
  // Anti-flicker (regla C.2/OB): las primitivas de rango_quantum son labels
  // (addLabel) y price lines (addPriceLine) ancladas a datos — no necesitan
  // reconstrucción en cada frame de pan puro (mismo criterio que patron_ruptura_quantum).
  renderOnPan: false,
}
